/**
 * ScoreManager — single source of truth for mission scoring.
 *
 * Collects stats during gameplay via events, computes a multi-dimensional
 * points-based score at mission end, and derives star ratings + letter grades.
 *
 * 13 scoring dimensions, 1400 pts max total.
 * Stars: 1★ ≥25%, 2★ ≥50%, 3★ ≥75%, Platinum ≥90% + 100%HP + 0 losses.
 */

import { EventBus, GameEvents } from '@/events/EventBus';

// ── Interfaces ──

export interface ScoreConfig {
  totalWaves: number;
  tier: number;
  mapCols: number;
  mapRows: number;
  tileSize: number;
  enemyBasesTotal: number;
  enemyBasesTotalHp: number;
  playerNodesTotal: number;
  startingResources: number;
  /** Override par time (seconds). Default = totalWaves * 30 */
  parTimeSeconds?: number;
}

export interface ScoreDimension {
  id: string;
  label: string;
  points: number;
  maxPoints: number;
  ratio: number;     // 0..1
  grade: string;      // S/A/B/C/D/E/F
  gradeColor: string; // hex color for the grade letter
  /** Optional detail text shown after the label (e.g. "2/3 (450 HP)") */
  detail?: string;
  /** Bonus dimensions add points but don't count toward maxScore (never penalize) */
  isBonus?: boolean;
}

export interface MissionScore {
  dimensions: ScoreDimension[];
  totalScore: number;
  maxScore: number;
  ratio: number;
  stars: number;       // 0-3
  isPlatinum: boolean; // 4th star
  /** Total wave time in milliseconds (for chrono display) */
  elapsedMs: number;
}

// ── Grade thresholds ──

function gradeFromRatio(r: number): { grade: string; color: string } {
  if (r >= 0.95) return { grade: 'S', color: '#ffdd00' };
  if (r >= 0.80) return { grade: 'A', color: '#44ff44' };
  if (r >= 0.65) return { grade: 'B', color: '#44aaff' };
  if (r >= 0.50) return { grade: 'C', color: '#cccc44' };
  if (r >= 0.35) return { grade: 'D', color: '#ff8844' };
  if (r >= 0.20) return { grade: 'E', color: '#ff4444' };
  return { grade: 'F', color: '#884444' };
}

// ── Live stats (accumulated during gameplay) ──

export interface LiveStats {
  // Time
  missionStartTime: number;
  missionEndTime: number;
  totalWaveTimeMs: number;

  // Combat
  enemiesKilled: number;
  maxKillStreak: number;
  streakEvents: number;       // distinct streaks of 3+
  currentStreak: number;      // live tracking

  // Precision
  shotsHit: number;
  shotsFired: number;

  // Pressure
  peakEnemiesAlive: number;

  // Perfect waves
  perfectWaves: number;       // waves without base damage
  waveBaseDamaged: boolean;   // flag for current wave

  // Economy
  resourcesEarned: number;
  resourcesSpent: number;

  // Variety
  uniqueDesigns: Set<string>;

  // Wave chaining
  wavesChained: number;

  // Bases
  enemyBasesDestroyed: number;
  enemyBaseHpEliminated: number;

  // Fog
  fogExplored: number;        // set at finalize
  fogTotal: number;

  // Robot distance
  totalRobotDistance: number;

  // Player base
  playerNodesDestroyed: number;
  hpRatio: number;

  // Meta
  wavesReached: number;
  robotsDeployed: number;
  robotsLost: number;
  won: boolean;

  // ── Robot composition flags (set during ROBOT_PLACED) ──
  hadFlyingRobot: boolean;       // at least one flying robot was deployed
  hadNonFlyingRobot: boolean;    // at least one ground robot was deployed
  hadNonFixedRobot: boolean;     // at least one robot without stab legs (mobile)
  hadSupportOnlyBot: boolean;    // at least one robot with zero weapons
  mineKills: number;             // enemies killed by mines
  camperBaseKills: number;       // enemy bases destroyed by fixed robots
  // ── Achievement tracking ──
  maxDamageSurvived: number;     // biggest single hit a robot survived this mission
  maxSingleHitDamage: number;    // biggest single hit dealt to an enemy this mission
  combosThisWave: number;        // running count of 3+ streaks in current wave
  maxCombosOneWave: number;      // best wave's combo count
  totalRicochets: number;        // ricochets this mission

  // ── Fun tracking ──
  funStuns: number;              // enemies stunned (new stuns, not extensions)
  funTerrainKills: number;       // enemies pushed into water/magma/chasm
  funBilliardBounces: number;    // wall bounces during billiard knockback
  funBilliardCollisions: number; // enemy-vs-enemy billiard collisions
  funJumperPushes: number;       // jumper push events
}

// ── ScoreManager singleton ──

class ScoreManagerClass {
  private config!: ScoreConfig;
  public stats!: LiveStats;
  private initialized = false;
  private baseHpMap = new Map<number, number>();

  /** Call at mission start (GameScene.create) */
  reset(config: ScoreConfig): void {
    this.config = config;
    this.stats = {
      missionStartTime: 0,
      missionEndTime: 0,
      totalWaveTimeMs: 0,
      enemiesKilled: 0,
      maxKillStreak: 0,
      streakEvents: 0,
      currentStreak: 0,
      shotsHit: 0,
      shotsFired: 0,
      peakEnemiesAlive: 0,
      perfectWaves: 0,
      waveBaseDamaged: false,
      resourcesEarned: 0,
      resourcesSpent: 0,
      uniqueDesigns: new Set(),
      wavesChained: 0,
      enemyBasesDestroyed: 0,
      enemyBaseHpEliminated: 0,
      fogExplored: 0,
      fogTotal: 0,
      totalRobotDistance: 0,
      playerNodesDestroyed: 0,
      hpRatio: 1,
      wavesReached: 0,
      robotsDeployed: 0,
      robotsLost: 0,
      won: false,
      hadFlyingRobot: false,
      hadNonFlyingRobot: false,
      hadNonFixedRobot: false,
      hadSupportOnlyBot: false,
      mineKills: 0,
      camperBaseKills: 0,
      maxDamageSurvived: 0,
      maxSingleHitDamage: 0,
      combosThisWave: 0,
      maxCombosOneWave: 0,
      totalRicochets: 0,
      funStuns: 0,
      funTerrainKills: 0,
      funBilliardBounces: 0,
      funBilliardCollisions: 0,
      funJumperPushes: 0,
    };
    this.baseHpMap.clear();
    this.bindEvents();
    this.initialized = true;
  }

  // ── Event bindings ──

  private boundHandlers: { event: string; fn: Function }[] = [];

  private bindEvents(): void {
    this.unbindEvents();
    const on = (event: string, fn: (...args: any[]) => void) => {
      EventBus.on(event, fn);
      this.boundHandlers.push({ event, fn });
    };

    on(GameEvents.ENEMY_DESTROYED, this.onEnemyDestroyed);
    on(GameEvents.WAVE_STARTED, this.onWaveStarted);
    on(GameEvents.WAVE_COMPLETED, this.onWaveCompleted);
    on(GameEvents.BASE_DAMAGED, this.onBaseDamaged);
    on(GameEvents.ENEMY_BASE_DESTROYED, this.onEnemyBaseDestroyed);
    on(GameEvents.ENEMY_BASE_DAMAGED, this.onEnemyBaseDamaged);
    on(GameEvents.ROBOT_PLACED, this.onRobotPlaced);
    on('mineKills', this.onMineKills);
    on('projectileHit', this.onProjectileHit);
    on('projectileMiss', this.onProjectileMiss);
    on('waveChained', this.onWaveChained);
    on('robotSurvivedHit', this.onRobotSurvivedHit);
    on('enemyDamageDealt', this.onEnemyDamageDealt);
    on(GameEvents.ARMOR_RICOCHET, this.onArmorRicochet);
    on(GameEvents.ENEMY_STUNNED, this.onEnemyStunned);
    on(GameEvents.TERRAIN_DEATH, this.onTerrainDeath);
    on(GameEvents.BILLIARD_WALL_BOUNCE, this.onBilliardBounce);
    on(GameEvents.BILLIARD_ENEMY_COLLISION, this.onBilliardCollision);
    on(GameEvents.JUMPER_PUSH, this.onJumperPush);
  }

  private unbindEvents(): void {
    for (const { event, fn } of this.boundHandlers) {
      EventBus.off(event, fn as any);
    }
    this.boundHandlers = [];
  }

  // ── Event handlers ──

  private onEnemyDestroyed = (): void => {
    this.stats.enemiesKilled++;
    this.stats.currentStreak++;
    if (this.stats.currentStreak >= 3 && this.stats.currentStreak === 3) {
      this.stats.streakEvents++;
      // Per-wave combo tracking
      this.stats.combosThisWave++;
      if (this.stats.combosThisWave > this.stats.maxCombosOneWave) {
        this.stats.maxCombosOneWave = this.stats.combosThisWave;
      }
    }
    if (this.stats.currentStreak > this.stats.maxKillStreak) {
      this.stats.maxKillStreak = this.stats.currentStreak;
    }
  };

  private onWaveStarted = (): void => {
    if (this.stats.missionStartTime === 0) {
      this.stats.missionStartTime = Date.now();
    }
    this.stats.waveBaseDamaged = false;
  };

  private onWaveCompleted = (): void => {
    this.stats.wavesReached++;
    this.stats.currentStreak = 0; // reset streak between waves
    this.stats.combosThisWave = 0; // reset per-wave combo counter
    if (!this.stats.waveBaseDamaged) {
      this.stats.perfectWaves++;
    }
  };

  private onBaseDamaged = (): void => {
    this.stats.waveBaseDamaged = true;
  };

  private onEnemyBaseDestroyed = (): void => {
    this.stats.enemyBasesDestroyed++;
    // "Camper" — if all deployed robots were fixed (stab legs only), count this
    if (this.stats.robotsDeployed > 0 && !this.stats.hadNonFixedRobot) {
      this.stats.camperBaseKills++;
    }
  };

  private onEnemyBaseDamaged = (data: { baseId: number; hp: number; maxHp: number }): void => {
    // Track last known HP per base to compute delta
    const prevHp = this.baseHpMap.get(data.baseId) ?? data.maxHp;
    const dmg = Math.max(0, prevHp - data.hp);
    this.stats.enemyBaseHpEliminated += dmg;
    this.baseHpMap.set(data.baseId, data.hp);
  };

  private onProjectileHit = (): void => {
    this.stats.shotsFired++;
    this.stats.shotsHit++;
  };

  private onProjectileMiss = (): void => {
    this.stats.shotsFired++;
  };

  private onWaveChained = (): void => {
    this.stats.wavesChained++;
  };

  /** Track robot composition for weird achievements */
  private onRobotPlaced = (robot: any): void => {
    if (!this.initialized || !robot) return;
    // Check flight capability
    if (typeof robot.canFly === 'function' && robot.canFly()) {
      this.stats.hadFlyingRobot = true;
    } else {
      this.stats.hadNonFlyingRobot = true;
    }
    // Check if robot is fixed (has stab legs = immovable)
    const isFixed = typeof robot.hasStabLegs === 'function' && robot.hasStabLegs();
    if (!isFixed) {
      this.stats.hadNonFixedRobot = true;
    }
    // Check if robot has zero weapons (support-only / pacifist)
    if (typeof robot.getWeapons === 'function' && robot.getWeapons().length === 0) {
      this.stats.hadSupportOnlyBot = true;
    }
  };

  /** Track mine kills */
  private onMineKills = (kills: number): void => {
    if (!this.initialized) return;
    this.stats.mineKills += kills;
  };

  /** Track biggest single hit survived by a robot */
  private onRobotSurvivedHit = (data: { damage: number }): void => {
    if (!this.initialized) return;
    if (data.damage > this.stats.maxDamageSurvived) {
      this.stats.maxDamageSurvived = data.damage;
    }
  };

  /** Track biggest single hit dealt to an enemy */
  private onEnemyDamageDealt = (data: { damage: number }): void => {
    if (!this.initialized) return;
    if (data.damage > this.stats.maxSingleHitDamage) {
      this.stats.maxSingleHitDamage = data.damage;
    }
  };

  /** Track armor ricochets */
  private onArmorRicochet = (): void => {
    if (!this.initialized) return;
    this.stats.totalRicochets++;
  };

  // ── Fun event handlers ──

  private onEnemyStunned = (): void => {
    if (!this.initialized) return;
    this.stats.funStuns++;
  };

  private onTerrainDeath = (): void => {
    if (!this.initialized) return;
    this.stats.funTerrainKills++;
  };

  private onBilliardBounce = (): void => {
    if (!this.initialized) return;
    this.stats.funBilliardBounces++;
  };

  private onBilliardCollision = (): void => {
    if (!this.initialized) return;
    this.stats.funBilliardCollisions++;
  };

  private onJumperPush = (): void => {
    if (!this.initialized) return;
    this.stats.funJumperPushes++;
  };

  // ── Called every frame during WAVE phase from GameScene.update ──

  updateWaveTime(deltaMs: number): void {
    if (!this.initialized) return;
    this.stats.totalWaveTimeMs += deltaMs;
  }

  /** Call from WaveManager.update to track peak enemies alive */
  updatePeakEnemies(aliveCount: number): void {
    if (!this.initialized) return;
    if (aliveCount > this.stats.peakEnemiesAlive) {
      this.stats.peakEnemiesAlive = aliveCount;
    }
  }

  /** Call from RobotPhysics after position update */
  addRobotDistance(dist: number): void {
    if (!this.initialized) return;
    this.stats.totalRobotDistance += dist;
  }

  /** Call from RobotManager.addRobot */
  recordDesign(name: string): void {
    if (!this.initialized) return;
    this.stats.uniqueDesigns.add(name);
    this.stats.robotsDeployed++;
  }

  recordResourceEarned(amount: number): void {
    if (!this.initialized) return;
    this.stats.resourcesEarned += amount;
  }

  recordResourceSpent(amount: number): void {
    if (!this.initialized) return;
    this.stats.resourcesSpent += amount;
  }

  recordRobotLost(): void {
    if (!this.initialized) return;
    this.stats.robotsLost++;
  }

  // ── Finalize: compute the score ──

  finalize(
    won: boolean,
    hpRatio: number,
    playerNodesDestroyed: number,
    fogExplored: number,
    fogTotal: number,
  ): MissionScore {
    this.stats.missionEndTime = Date.now();
    this.stats.won = won;
    this.stats.hpRatio = hpRatio;
    this.stats.playerNodesDestroyed = playerNodesDestroyed;
    this.stats.fogExplored = fogExplored;
    this.stats.fogTotal = fogTotal;

    const score = this.computeScore();
    this.unbindEvents();
    this.initialized = false;
    return score;
  }

  private computeScore(): MissionScore {
    const s = this.stats;
    const c = this.config;
    const dims: ScoreDimension[] = [];

    const addDim = (id: string, label: string, points: number, maxPoints: number, isBonus = false) => {
      const ratio = maxPoints > 0 ? Math.min(1, points / maxPoints) : 1;
      const { grade, color } = gradeFromRatio(ratio);
      dims.push({ id, label, points: Math.round(points), maxPoints, ratio, grade, gradeColor: color, isBonus });
    };

    // 1. Speed (350 pts) — par time based, highest weight
    const parTime = c.parTimeSeconds ?? c.totalWaves * 30;
    const elapsedS = s.totalWaveTimeMs / 1000;
    const speedRatio = Math.max(0, 1 - elapsedS / (parTime * 2));
    addDim('speed', 'Vitesse', speedRatio * 350, 350);

    // 2. Combos (100 pts) — max streak + streak events
    const targetStreak = 5 + c.tier;
    const streakPart = Math.min(1, s.maxKillStreak / targetStreak) * 60;
    const eventPart = Math.min(1, s.streakEvents / Math.max(1, c.totalWaves)) * 40;
    addDim('combos', 'Combos', streakPart + eventPart, 100);

    // 3. Robot variety (80 pts) — BONUS: rewards diversity without penalizing focused builds
    const targetVariety = Math.min(4, Math.ceil(c.totalWaves / 3));
    const varietyRatio = Math.min(1, s.uniqueDesigns.size / Math.max(1, targetVariety));
    addDim('variety', 'Variété', varietyRatio * 80, 80, true);

    // 4. Economy (80 pts) — BONUS: rewards saving without penalizing spending
    const moneySaved = Math.max(0, s.resourcesEarned - s.resourcesSpent);
    const targetSaved = c.startingResources * 1.5;
    const econRatio = Math.min(1, moneySaved / Math.max(1, targetSaved));
    addDim('economy', 'Économie', econRatio * 80, 80, true);

    // 5. Wave chaining (80 pts)
    const chainMax = Math.max(1, c.totalWaves - 1);
    const chainRatio = Math.min(1, s.wavesChained / chainMax);
    addDim('chaining', 'Enchaînement', chainRatio * 80, 80);

    // 6. Enemy bases (200 pts) — auto-max if no bases on map
    if (c.enemyBasesTotal > 0) {
      const baseRatio = s.enemyBasesDestroyed / c.enemyBasesTotal;
      addDim('enemyBases', 'Bases ennemies', baseRatio * 200, 200);
      // Add detail: "X/Y (eliminatedHP/totalHP HP)"
      const baseDim = dims[dims.length - 1];
      baseDim.detail = `${s.enemyBasesDestroyed}/${c.enemyBasesTotal} (${Math.round(s.enemyBaseHpEliminated)}/${c.enemyBasesTotalHp}HP)`;
    } else {
      addDim('enemyBases', 'Bases ennemies', 200, 200);
    }

    // 7. Base HP preserved (200 pts) — biggest weight
    addDim('baseHp', 'HP préservée', s.hpRatio * 200, 200);

    // 8. Precision (80 pts) — shots hit / fired
    const precisionRatio = s.shotsFired > 0 ? s.shotsHit / s.shotsFired : 1;
    addDim('precision', 'Précision', precisionRatio * 80, 80);

    // 9. Pressure survived (50 pts) — peak enemies alive vs target
    const targetPressure = 10 + c.tier * 3;
    const pressureRatio = Math.min(1, s.peakEnemiesAlive / targetPressure);
    addDim('pressure', 'Pression max', pressureRatio * 50, 50);

    // 10. Perfect waves (60 pts) — waves without base damage
    const perfectRatio = c.totalWaves > 0 ? s.perfectWaves / c.totalWaves : 1;
    addDim('perfectWaves', 'Vagues parfaites', perfectRatio * 60, 60);

    // 11. Fog discovery (50 pts) — BONUS
    const fogRatio = s.fogTotal > 0 ? s.fogExplored / s.fogTotal : 1;
    addDim('fog', 'Fog découvert', fogRatio * 50, 50, true);

    // 12. Robot distance (50 pts) — BONUS
    const targetDist = c.mapCols * c.mapRows * c.tileSize * 0.3;
    const distRatio = Math.min(1, s.totalRobotDistance / Math.max(1, targetDist));
    addDim('distance', 'Distance', distRatio * 50, 50, true);

    // 13. Fun (100 pts) — BONUS: spectacular events reward, never penalize
    const funEvents = s.funStuns + s.funTerrainKills * 3 + s.funBilliardBounces
      + s.funBilliardCollisions * 2 + s.totalRicochets * 2 + s.funJumperPushes + s.mineKills * 2;
    const targetFun = 10 + c.tier * 3 + c.totalWaves * 2;
    const funRatio = Math.min(1, funEvents / Math.max(1, targetFun));
    addDim('fun', 'Fun', funRatio * 100, 100, true);
    // Detail: breakdown of fun events
    const funParts: string[] = [];
    if (s.funStuns > 0) funParts.push(`${s.funStuns} stuns`);
    if (s.funTerrainKills > 0) funParts.push(`${s.funTerrainKills} noyés`);
    if (s.funBilliardBounces + s.funBilliardCollisions > 0)
      funParts.push(`${s.funBilliardBounces + s.funBilliardCollisions} billard`);
    if (s.totalRicochets > 0) funParts.push(`${s.totalRicochets} ricochets`);
    if (s.funJumperPushes > 0) funParts.push(`${s.funJumperPushes} pushes`);
    if (s.mineKills > 0) funParts.push(`${s.mineKills} mines`);
    if (funParts.length > 0) dims[dims.length - 1].detail = funParts.join(', ');

    // ── Compensation: dominant performance compensates for weak dimensions ──
    // When a player excels in many dimensions, weak "opportunity" dimensions
    // (combos, variety, chaining etc.) are lifted so speedruns / dominant
    // victories aren't unfairly penalized by dimensions they couldn't engage with.

    const sorted = [...dims].sort((a, b) => b.ratio - a.ratio);
    const topHalf = sorted.slice(0, Math.ceil(sorted.length / 2));
    const dominance = topHalf.reduce((acc, d) => acc + d.ratio * d.maxPoints, 0)
                    / topHalf.reduce((acc, d) => acc + d.maxPoints, 0);

    if (dominance > 0.70) {
      const LIFT = 0.75; // weak dims' floor = dominance × LIFT × maxPoints
      for (const dim of dims) {
        if (dim.ratio < 0.5) {
          const floor = Math.round(dominance * LIFT * dim.maxPoints);
          if (floor > dim.points) {
            dim.points = floor;
            dim.ratio = dim.maxPoints > 0 ? dim.points / dim.maxPoints : 1;
            const { grade, color } = gradeFromRatio(dim.ratio);
            dim.grade = grade;
            dim.gradeColor = color;
          }
        }
      }
    }

    // Totals (after compensation)
    // Bonus dims add to totalScore but NOT to maxScore — they can only help, never penalize
    const coreScore = dims.filter(d => !d.isBonus).reduce((sum, d) => sum + d.points, 0);
    const coreMax = dims.filter(d => !d.isBonus).reduce((sum, d) => sum + d.maxPoints, 0);
    const bonusScore = dims.filter(d => d.isBonus).reduce((sum, d) => sum + d.points, 0);
    const totalScore = coreScore + bonusScore;
    const maxScore = coreMax; // max is core only — bonus is extra
    // Ratio = core performance (for stars/platinum). Bonus doesn't inflate or deflate it.
    const ratio = coreMax > 0 ? coreScore / coreMax : 0;

    // Stars
    let stars: number;
    if (!s.won) {
      stars = 0;
    } else if (ratio >= 0.75) {
      stars = 3;
    } else if (ratio >= 0.50) {
      stars = 2;
    } else if (ratio >= 0.25) {
      stars = 1;
    } else {
      stars = 0;
    }

    // Player base nodes penalty: -1 star per node lost
    if (s.playerNodesDestroyed > 0) {
      stars = Math.max(0, stars - s.playerNodesDestroyed);
    }

    // Platinum: classic path (90%+ ratio) OR dominant victory OR speedrun
    const classicPlatinum = s.won && stars === 3 && ratio >= 0.90
      && s.hpRatio >= 1.0 && s.robotsLost === 0;

    // Dominant victory: S-rank in all core combat dimensions + no losses
    const coreIds = ['speed', 'enemyBases', 'baseHp', 'precision'];
    const allCoreSRank = coreIds.every(id => {
      const d = dims.find(dim => dim.id === id);
      return d && d.ratio >= 0.90;
    });
    const dominantPlatinum = s.won && allCoreSRank && s.hpRatio >= 1.0
      && s.robotsLost === 0 && dominance >= 0.85;

    // Speedrun: ultra-fast clear (< 25% par time) with no damage → platinum
    const speedrunPlatinum = s.won && elapsedS < parTime * 0.25
      && s.hpRatio >= 1.0 && s.robotsLost === 0;

    const isPlatinum = classicPlatinum || dominantPlatinum || speedrunPlatinum;
    if (isPlatinum) stars = 3; // ensure 3 stars for platinum

    return { dimensions: dims, totalScore: Math.round(totalScore), maxScore, ratio, stars, isPlatinum, elapsedMs: s.totalWaveTimeMs };
  }

  /** Get current stats (for mid-game UI if needed) */
  getStats(): LiveStats | null {
    return this.initialized ? this.stats : null;
  }
}

export const ScoreManager = new ScoreManagerClass();
